import React, { useState } from 'react'
import { URL } from './Constants'
import { useUser } from '../UserContext'
import { db } from '../Firebase'
import { collection, addDoc, serverTimestamp, onSnapshot, query, orderBy, limit,where } from 'firebase/firestore'
import { Link, useNavigate } from 'react-router-dom'
import { ChevronDown, Home, MapPin, MessageSquare, BarChart2, Info, Mail, User } from 'lucide-react'

function GeminiApi() {

  const navigate=useNavigate()
  const {userData,currentUser} = useUser()
  const [question, setQuestion] = useState('')
  const [result, setResult] = useState(undefined)
  const [isLoading, setIsLoading] = useState(false)
  const [messages, setMessages] = useState([])
  const [isNavDropdownOpen, setIsNavDropdownOpen] = useState(false)

// Fetch existing messages
React.useEffect(() => {
  if (!currentUser?.uid) return;

  const q = query(
    collection(db, 'messages'),
    where('userId', '==', currentUser.uid)
  );
 
  const unsubscribe =onSnapshot(q, (snapshot) => {
    const messageList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    messageList.sort((a, b) => a.createdAt?.seconds - b.createdAt?.seconds);
    setMessages(messageList);
  });
  

  return () => unsubscribe();
}, [currentUser?.uid]);



  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.nav-dropdown')) {
        setIsNavDropdownOpen(false)
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navigationLinks = [
    { name: 'Home', path: '/', icon: <Home className="w-4 h-4" /> },
    { name: 'Local Access Hubs', path: '/hubs', icon: <MapPin className="w-4 h-4" /> },
    { name: 'Community Forum', path: '/forum', icon: <MessageSquare className="w-4 h-4" /> },
    { name: 'Transparency Dashboard', path: '/dashboard', icon: <BarChart2 className="w-4 h-4" /> },
    { name: 'About Us', path: '/about', icon: <Info className="w-4 h-4" /> },
    { name: 'Contact', path: '/contact', icon: <Mail className="w-4 h-4" /> },
  ]

  const about = "VitaBridge is a platform that provides access to vital resources, knowledge, and community support to enhance food security and create sustainable livelihoods."
  const payload = {
    "contents": [
      {
        "parts": [
          {
            "text":"You are an AI assistant for VitaBridge and vitabridge is "+ about +". Answer the user's question: "+ question
          }
        ]
      }
    ]
  }

  const askQuestion = async () => {
    if (!question || !question.trim()) return
    
    try {
      setIsLoading(true)
      
      // Save user question to Firebase
      const userMessageRef = await addDoc(collection(db, 'messages'), {
        type: 'user',
        content: question.trim(),
        userId: currentUser?.uid || 'anonymous',
        userName: userData?.name || 'Anonymous',
        createdAt: serverTimestamp(),
        timestamp: new Date().toISOString()
      })

      let response = await fetch(URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })
      
      response = await response.json()
      const GeminiResult = response?.candidates?.[0]?.content?.parts?.[0]?.text || 'No response received.'
      
      // Save AI response to Firebase
      await addDoc(collection(db, 'messages'), {
        type: 'ai',
        content: GeminiResult,
        userId: currentUser?.uid,
        userName: 'Vita AI',
        
        createdAt: serverTimestamp(),
        timestamp: new Date().toISOString(),
        relatedTo: userMessageRef.id // Link to user's question
      })


      
      setQuestion('') // Clear input after successful submission
      
    } catch (err) {
      console.error('Error:', err)
      setResult('Sorry, something went wrong. Please try again.')
      
      // Save error message to Firebase
      await addDoc(collection(db, 'messages'), {
        type: 'error',
        content: 'Sorry, something went wrong. Please try again.',
        userId: currentUser?.uid,
        userName: 'System',
        
        createdAt: serverTimestamp(),
        timestamp: new Date().toISOString(),
        error: true
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className='min-h-screen bg-gradient-to-br from-zinc-900 via-zinc-950 to-black text-white'>
      {/* Fixed Sidebar - Hidden on mobile */}
      <div className="hidden md:block fixed left-0 top-0 h-full w-64 border-r border-zinc-800/80 bg-zinc-900/50 backdrop-blur-sm z-40">
        <div className="h-full flex flex-col">
          <div className="px-6 py-6 border-b border-zinc-800/70">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-2xl bg-gradient-to-tr from-indigo-500 via-fuchsia-500 to-rose-500" />
              <div className="text-left">
                <p className="text-lg font-semibold tracking-tight">VitaBridge</p>
                <p className="text-xs text-zinc-400">AI Assistant</p>
              </div>
            </div>
          </div>
          <nav className="p-4 space-y-2 text-sm">
            <button className="w-full text-left px-3 py-2 rounded-xl hover:bg-zinc-800/50 transition">New Chat</button>
            <button className="w-full text-left px-3 py-2 rounded-xl hover:bg-zinc-800/50 transition">Saved</button>
            <button className="w-full text-left px-3 py-2 rounded-xl hover:bg-zinc-800/50 transition">Settings</button>
          </nav>
          <div className="mt-auto p-4 border-t border-zinc-800/70 text-xs text-zinc-400">
            <p>Logged in as</p>
            <p className="text-zinc-300">{userData?.name || 'Guest'}</p>
          </div>
        </div>
      </div>

      {/* Mobile Header - Only visible on mobile */}
      <div className="md:hidden fixed top-0 left-0 right-0 bg-zinc-900/90 backdrop-blur-sm border-b border-zinc-800/70 z-40">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-xl bg-gradient-to-tr from-indigo-500 via-fuchsia-500 to-rose-500" />
            <div>
              <p className="text-sm font-semibold tracking-tight">VitaBridge</p>
              <p className="text-xs text-zinc-400">AI Assistant</p>
            </div>
          </div>
          <div className="text-xs text-zinc-400">
            <p onClick={()=>navigate('/')}>Home</p>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className={`pt-0 md:pt-0 ${window.innerWidth >= 768 ? 'md:ml-64' : ''}`}>
        {/* Header */}
        <div className="px-4 md:px-8 py-4 md:py-6 border-b border-zinc-800/70 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex-1">
            <h1 className="text-lg md:text-xl font-semibold tracking-tight">Welcome {userData?.name}</h1>
            <p className="text-xs md:text-sm text-zinc-400">Ask anything. Get helpful answers.</p>
          </div>
          
          {/* Navigation Dropdown */}
          {window.innerWidth >= 768 && <div className="relative nav-dropdown">
            <button
              onClick={() => setIsNavDropdownOpen(!isNavDropdownOpen)}
              className="flex items-center gap-2 px-4 py-2 bg-zinc-900/60 border border-zinc-800/70 rounded-xl hover:bg-zinc-800/60 transition-colors"
            >
              <span className="text-sm text-zinc-300">Navigate</span>
              <ChevronDown className={`w-4 h-4 text-zinc-400 transition-transform ${isNavDropdownOpen ? 'rotate-180' : ''}`} />
            </button>
            
            {isNavDropdownOpen && (
              <div className="absolute right-0 mt-2 w-56 bg-zinc-900/95 backdrop-blur-md rounded-xl shadow-lg py-2 z-50 border border-zinc-800/70">
                {navigationLinks.map((link) => (
                  <Link
                    key={link.name}
                    to={link.path}
                    onClick={() => setIsNavDropdownOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 text-sm text-zinc-300 hover:bg-zinc-800/50 hover:text-white transition-colors"
                  >
                    {link.icon}
                    {link.name}
                  </Link>
                ))}
              </div>
            )}
          </div>}
        </div>

        {/* Chat Area */}
        <div className="relative flex-1 px-4 md:px-8 py-4 md:py-8 overflow-hidden min-h-[calc(100vh-200px)]">
          <div className="absolute inset-0 opacity-30 pointer-events-none">
            <div className="h-full w-full bg-[radial-gradient(1000px_600px_at_120%_-10%,rgba(79,70,229,0.25),transparent_60%),radial-gradient(800px_500px_at_-20%_110%,rgba(236,72,153,0.18),transparent_60%)]" />
          </div>

          <div className="relative h-full max-w-4xl mx-auto flex flex-col">
            <div className="flex-1 overflow-y-auto space-y-4 pr-2 pb-24 md:pb-32">
              {messages.length === 0 && !isLoading && (
                <div className="mx-auto text-center text-zinc-400 mt-8 md:mt-14">
                  <p className="text-sm">Start a conversation by asking a question below</p>
                </div>
              )}

              {/* Display all messages */}
              {messages.map((message) => (
                <div key={message.id} className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                  {message.type === 'user' ? (
                    <div className="max-w-[85%] rounded-2xl rounded-br-md bg-indigo-600/90 text-white px-3 md:px-4 py-2 md:py-3 shadow-md">
                      <p className="text-sm leading-relaxed whitespace-pre-line">{message.content}</p>
                    </div>
                  ) : (
                    <div className="flex items-start gap-2 md:gap-3">
                      <div className="h-6 md:h-8 w-6 md:w-8 shrink-0 rounded-lg md:rounded-xl bg-gradient-to-tr from-indigo-500 via-fuchsia-500 to-rose-500" />
                      <div className={`max-w-[85%] rounded-2xl rounded-tl-md px-3 md:px-4 py-2 md:py-3 text-zinc-100 shadow-[0_0_1px_1px_rgba(255,255,255,0.02)] ${
                        message.type === 'error' 
                          ? 'bg-red-900/70 border border-red-800/70' 
                          : 'bg-zinc-900/70 border border-zinc-800/70'
                      }`}>
                        <p className="text-sm leading-relaxed whitespace-pre-line">{message.content}</p>
                        <p className="text-xs text-zinc-400 mt-2">
                          {message.timestamp ? new Date(message.timestamp).toLocaleTimeString() : 'Just now'}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {/* Loading indicator */}
              {isLoading && (
                <div className="flex items-start gap-2 md:gap-3">
                  <div className="h-6 md:h-8 w-6 md:w-8 shrink-0 rounded-lg md:rounded-xl bg-gradient-to-tr from-indigo-500 via-fuchsia-500 to-rose-500" />
                  <div className="max-w-[85%] rounded-2xl rounded-tl-md bg-zinc-900/70 border border-zinc-800/70 px-3 md:px-4 py-2 md:py-3 text-zinc-300 shadow-[0_0_1px_1px_rgba(255,255,255,0.02)]">
                    <div className="flex items-center gap-2">
                      <span className="h-1.5 md:h-2 w-1.5 md:w-2 rounded-full bg-zinc-400 animate-bounce" style={{animationDelay:'0ms'}} />
                      <span className="h-1.5 md:h-2 w-1.5 md:w-2 rounded-full bg-zinc-400 animate-bounce" style={{animationDelay:'150ms'}} />
                      <span className="h-1.5 md:h-2 w-1.5 md:w-2 rounded-full bg-zinc-400 animate-bounce" style={{animationDelay:'300ms'}} />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Fixed Bottom Input Form */}
      <div className="fixed bottom-0 left-0 right-0 bg-zinc-900/95 backdrop-blur-md border-t border-zinc-800/70 z-50">
        <div className="px-3 md:px-4 py-2 md:py-4">
          <div className="max-w-4xl mx-auto">
            <form onSubmit={(e) => { e.preventDefault(); askQuestion(); }} className="flex flex-col gap-3">
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                <input
                  type="text"
                  placeholder='Ask me anything...'
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  className="flex-1 h-14 md:h-16 p-4 border border-zinc-700 rounded-2xl bg-zinc-800/70 text-zinc-100 placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 text-base md:text-lg"
                />
                <button
                  type="submit"
                  disabled={isLoading || !question.trim()}
                  className="w-full sm:w-auto h-14 md:h-16 px-6 md:px-8 rounded-2xl bg-gradient-to-r from-indigo-500 via-fuchsia-500 to-rose-500 text-white font-medium text-base md:text-lg shadow-lg shadow-fuchsia-500/20 hover:shadow-fuchsia-500/30 hover:brightness-110 active:scale-[0.98] transition disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      <span>Generating...</span>
                    </div>
                  ) : (
                    'Ask'
                  )}
                </button>
              </div>
              <p className="text-[10px] md:text-[11px] text-center text-zinc-500 px-2">AI can make mistakes. Consider checking important information.</p>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}

export default GeminiApi